import React from 'react';

/**
 * The FeatureAppWrap only sets max-width and max-height. This function patches
 * the styles so that the element expands to the size of the loading UI, so that
 * our element width calculations are correct.
 */
function patchOneHubFeatureAppWrapStyles(parentElement: HTMLElement | null): void {
  if (parentElement?.className.includes('FeatureAppWrap')) {
    parentElement.style.top = '0';
    parentElement.style.right = '0';
    parentElement.style.bottom = '0';
    parentElement.style.left = '0';
  }
}

export function useElementWidth(): [number, (element: HTMLElement | null) => void] {
  const [elementWidth, setElementWidth] = React.useState(0);
  const elementRef = React.useRef<HTMLElement | null>(null);

  const ref = React.useCallback((element: HTMLElement | null) => {
    elementRef.current = element;

    if (elementRef.current !== null) {
      patchOneHubFeatureAppWrapStyles(elementRef.current.parentElement);
      setElementWidth(elementRef.current.clientWidth);
    }
  }, []);

  React.useEffect(() => {
    const handleResize = () => {
      if (elementRef.current) {
        setElementWidth(elementRef.current.clientWidth);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  React.useLayoutEffect(() => {
    if (elementRef.current) {
      const elementWidthAfterUpdate = elementRef.current.clientWidth;

      if (elementWidthAfterUpdate !== elementWidth) {
        setElementWidth(elementWidthAfterUpdate);
      }
    }
  }, [elementWidth]);

  return [elementWidth, ref];
}
